import { ChevronDown, ChevronUp, Eye, EyeOff, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { BlockLayout, BlockLayoutDef } from '@/lib/use-block-layout';

/**
 * Trigger-Button + Dialog zum Ein-/Ausblenden und Umsortieren bestehender
 * Seiten-Blöcke (BL-219). Auf/Ab-Buttons statt Drag&Drop — siehe
 * `use-block-layout.ts` für die Scope-Begründung.
 */
export function BlockLayoutMenu({
  blocks,
  layout,
}: {
  blocks: BlockLayoutDef[];
  layout: BlockLayout;
}) {
  const labelById = new Map(blocks.map((b) => [b.id, b.label]));

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          title="Sichtbarkeit und Reihenfolge der Blöcke auf dieser Seite anpassen"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Ansicht
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ansicht anpassen</DialogTitle>
          <DialogDescription>
            Blöcke ein-/ausblenden und in eigener Reihenfolge anordnen — nur in diesem Browser
            gespeichert.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1">
          {layout.order.map((id, i) => {
            const hidden = layout.isHidden(id);
            return (
              <li
                key={id}
                className={cn(
                  'flex items-center gap-2 rounded-md border border-hair px-2.5 py-1.5',
                  hidden && 'opacity-50',
                )}
              >
                <span className="flex-1 truncate text-sm">{labelById.get(id) ?? id}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => layout.moveUp(id)}
                  disabled={i === 0}
                  title="Nach oben"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => layout.moveDown(id)}
                  disabled={i === layout.order.length - 1}
                  title="Nach unten"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => layout.toggleVisible(id)}
                  title={hidden ? 'Einblenden' : 'Ausblenden'}
                >
                  {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={layout.reset}>
            <RotateCcw className="h-3.5 w-3.5" />
            Zurücksetzen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
